import { GENERIC_IDP_ID, Method } from 'consts';
import { useAuthContext } from 'contexts/AuthContext';
import {
	useCurrentGuideIdPID,
	useIdPConfigContext
} from 'contexts/IdPConfigContext';
import React from 'react';
import { useQuery } from 'react-query';
import { IResIdPs } from 'types/api';
import { get } from './api';
import { API_ROUTES } from './apiRoutes';
import { useAppConfig } from './appConfig';
import QueryKeys from './queryKeys';

const getIdPs = async () => {
	const res = await get<IResIdPs>(API_ROUTES.ssoTemplates);

	return res.data;
};

const useIdPs = () => {
	const { authRes } = useAuthContext();
	const { data: appConfig } = useAppConfig();

	const { features } = appConfig || {};

	const filterIdpMethods = React.useMemo(
		() => (data: IResIdPs) => ({
			...data,
			data: data.data.map((idp) => ({
				...idp,
				methods: idp.methods.filter((method: Method) => {
					// XAA is never an SSO method - it has its own gallery (useXaaIdPs)
					if (method === Method.Xaa) {
						return false;
					}
					if (method === Method.Saml) {
						return !features?.samlDisabled;
					}
					return !features?.oidcDisabled; // method === Method.Oidc
				})
			}))
		}),
		[features]
	);

	return useQuery({
		queryKey: [QueryKeys.idps],
		queryFn: getIdPs,
		select: filterIdpMethods,
		staleTime: Infinity,
		enabled: authRes.verified
	});
};

const useIdP = (id?: string | null) => {
	const { authRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.idps],
		queryFn: getIdPs,
		enabled: authRes.verified && !!id,
		select: (data) => data.data.find((idp) => idp.id === id),
		staleTime: Infinity
	});
};

const getScimIdPs = (data: IResIdPs) =>
	data.data.filter(
		(idp) => idp.scimMethods?.length || idp.id === GENERIC_IDP_ID
	);

const useScimIdPs = () => {
	const { authRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.idps],
		queryFn: getIdPs,
		select: getScimIdPs,
		enabled: authRes.verified
	});
};

// XAA (Cross-App Access / ID-JAG) providers are sourced from the SAME content catalog
// (/v1/ssotemplates) as SSO/SCIM - never fabricated in code. Any catalog entry
// advertising the xaa method is included, plus the generic idp which carries the
// generic/manual XAA guide (exactly like SCIM's generic option).
const getXaaIdPs = (data: IResIdPs) =>
	data.data.filter(
		(idp) => idp.methods?.includes(Method.Xaa) || idp.id === GENERIC_IDP_ID
	);

const useXaaIdPs = () => {
	const { authRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.idps],
		queryFn: getIdPs,
		select: getXaaIdPs,
		enabled: authRes.verified
	});
};

const useCurrentIdP = () => {
	const { idpID } = useIdPConfigContext();
	return useIdP(idpID);
};

const useCurrentScimIdP = () => {
	const { scimIdpID } = useIdPConfigContext();
	return useIdP(scimIdpID);
};

// The current XAA idp - the content-sourced provider selected for XAA (its id lives in
// the dedicated xaaIdpID slot, never the SSO idpID).
const useCurrentXaaIdP = () => {
	const { xaaIdpID } = useIdPConfigContext();
	return useIdP(xaaIdpID);
};

// The content idp for the CURRENT guide type (SSO/SCIM/XAA). Used by shared components
// like the attribute-mapping step so they resolve the right provider in every context.
const useCurrentGuideIdP = () => {
	const idpID = useCurrentGuideIdPID();
	return useIdP(idpID);
};

export {
	useCurrentGuideIdP,
	useCurrentIdP,
	useCurrentScimIdP,
	useCurrentXaaIdP,
	useIdPs,
	useScimIdPs,
	useXaaIdPs
};
